import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/GlobalStyle';
import CarregandoDna from './ui/CarregandoDna';

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
  fullScreen?: boolean;
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const FullScreenWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.background};
  z-index: 9999;
`;

const InlineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.lg};
`;

const Spinner = styled.div<{ $size: string }>`
  width: ${props => props.$size};
  height: ${props => props.$size};
  border: 3px solid ${theme.colors.border};
  border-top-color: ${theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const Message = styled.p`
  margin-top: ${theme.spacing.md};
  color: ${theme.colors.textSecondary};
  font-size: 14px;
`;

/*
 * Na ficha do paciente, a espera de tela cheia é a mesma hélice da ficha (10/10). No F5 o portal passa por três
 * esperas antes da ficha (login, o pedaço da página, a conferência do acesso), todas por aqui; com o círculo, a
 * hélice só aparecia no fim, e no recarregar quase não dava para ver. O palco desconta o menu lateral (aberto ou
 * recolhido) e o cabeçalho do celular, para a hélice ficar no mesmo ponto em que a ficha a desenha (AdminLayout
 * centralizado) e não pular quando o layout chega.
 */
const naFichaDoPaciente = () =>
  typeof window !== 'undefined' && /^\/admin\/patients\/[^/]+/.test(window.location.pathname);
const menuRecolhido = () => {
  try { return localStorage.getItem('sidebar_collapsed') === 'true'; } catch { return false; }
};
const PalcoDaFicha = styled.div<{ $menu: number }>`
  position: fixed; inset: 0; z-index: 9999; box-sizing: border-box;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: ${theme.spacing.lg} ${theme.spacing.md} ${theme.spacing.lg} calc(${p => p.$menu}px + ${theme.spacing.md});
  background: ${theme.colors.background};
  @media (max-width: 768px) { padding: 60px ${theme.spacing.md} ${theme.spacing.lg}; }
`;

const sizeMap = {
  small: '20px',
  medium: '32px',
  large: '48px',
};

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'medium',
  message,
  fullScreen = false,
}) => {
  const { i18n } = useTranslation();
  if (fullScreen && naFichaDoPaciente()) {
    return (
      <PalcoDaFicha $menu={menuRecolhido() ? 68 : 230}>
        <CarregandoDna
          titulo="Essentia"
          mensagem={i18n.language === 'pt' ? 'Preparando inteligência clínica…' : 'Preparing clinical intelligence…'}
        />
      </PalcoDaFicha>
    );
  }
  const Wrapper = fullScreen ? FullScreenWrapper : InlineWrapper;

  return (
    <Wrapper>
      <Spinner $size={sizeMap[size]} />
      {message && <Message>{message}</Message>}
    </Wrapper>
  );
};

export default LoadingSpinner;
